import { Dispatch, Action, MiddlewareAPI, UnknownAction } from 'redux'

import { Either } from '@owl-nest/monad'
import { api } from '@owl-nest/api-client/latest'
import * as env from '@owl-nest/config'

import { internalError } from './messages/actions'

export type PromiseAction<STATE, RETURN = any, EXTRA = {}> = {
  types: [string, string, string]
  promise: (dispatch: Dispatch<UnknownAction>, state: STATE) => Promise<RETURN>
} & EXTRA

export function middleware<STATE>(store: MiddlewareAPI<Dispatch, STATE>) {
  return (next: (action: unknown) => unknown) =>
    async (action: unknown): Promise<unknown> => {
      if (!action || Object.prototype.toString.call(action) !== '[object Object]') {
        throw new Error('Expected an object ’action’')
      }

      if (!isPromiseAction<STATE>(action)) {
        return next(action)
      }

      if (!isCorrectTypes(action)) {
        throw new Error('Expected an array of three string types')
      }

      if (!isCorrectPromise(action)) {
        throw new Error("Expected a function 'promise'")
      }

      const {
        types: [REQUEST_TYPE, SUCCESS_TYPE, FAILURE_TYPE],
        promise,
        ...rest
      } = action

      // dispatch start request
      next({ ...rest, type: REQUEST_TYPE })

      try {
        const result = await promise(store.dispatch, store.getState())

        // If result is an Either, if it is left there is a failure,if it's a right there is a success
        if (result instanceof Either) {
          result.caseOf<void>({
            left: (failure) => {
              if (failure.status && failure.bypassStatusAutomaticActions !== true) {
                if (failure.status > 422 || failure.status === 405) {
                  next(internalError())
                }
                if (failure.status === 403) {
                  redirectToForbidden()
                }
                if (failure.status === 401 && failure.doNotRedirectToSignIn !== true) {
                  redirectToSignin(failure.url)
                }
              }

              next({ ...rest, failure, type: FAILURE_TYPE })
            },
            right: (response) => {
              next({ ...rest, success: response, type: SUCCESS_TYPE })
            },
          })
        } else {
          next({ ...rest, success: result, type: SUCCESS_TYPE })
        }
        return result
      } catch (error) {
        console.log('Error while dispatching promise action', error)
        throw error
      }
    }

  function redirectToSignin(endpoint: string): void {
    const next = encodeURIComponent(window.location.href)
    const lang = env.getUserConfig().lang
    const doNotRedirectEndpoints = [api.endpoints.me.url]

    if (doNotRedirectEndpoints.includes(endpoint)) {
      return
    }

    const redirectUrl = env.SIGNIN_URL + `?next=${next}&lang=${lang}`
    window.location.href = redirectUrl
  }

  function redirectToForbidden(): void {
    const next = encodeURIComponent(window.location.href)
    const lang = env.getUserConfig().lang

    window.location.href = env.FORBIDDEN_URL + `?next=${next}&lang=${lang}`
  }
}

function isPromiseAction<STATE>(action: any): action is PromiseAction<STATE> {
  return action.promise !== undefined
}

function isCorrectPromise(action: PromiseAction<any>): boolean {
  return typeof action.promise === 'function'
}

function isCorrectTypes(action: PromiseAction<any>): boolean {
  if (!Array.isArray(action.types)) {
    return false
  }
  if (action.types.length !== 3) {
    return false
  }
  const areTypesAllStrings = action.types.every((type) => typeof type === 'string')
  if (!areTypesAllStrings) {
    return false
  }
  return true
}

declare module 'redux' {
  /*
   * Overload to add promise support to Redux's dispatch() function.
   * Useful for react-redux or any other library which could use this type.
   */
  export interface Dispatch<A extends Action = UnknownAction> {
    <STATE = any, RETURN = any, EXTRA = {}>(promiseAction: PromiseAction<STATE, RETURN, EXTRA>): Promise<RETURN>
  }
}
